import { useRef, type KeyboardEvent, type ReactElement, type ReactNode } from "react";
import { CentredRow } from "./Centred.tsx";
import { classes } from "./classes.ts";
import { Glyph, type GlyphName } from "./Glyph.tsx";
import { useInteraction } from "./interaction.tsx";
import { KIT_METRICS, centreOffset, listRowContent, listRowHeight, logical } from "./metrics.ts";
import { choiceForKey, nextEnabled } from "./state.ts";

/**
 * A list to choose from (brief section 5.1), such as the characters of an account: a `listbox` that holds
 * only its `option` rows, one of them selected. A click or a tap on a row selects it; with the keyboard the
 * list is one tab stop, the up and down arrows, Home and End move the selection, skipping disabled rows and
 * wrapping, Space selects the focused row and Enter acts on it, as the page's own button for that action
 * does (brief rule 23). A list with no rows shows its empty state in place of the listbox, never inside it.
 * Given a height, the listbox scrolls inside it with the kit's scrollbar of whole logical pixels, by whole
 * rows: each row's top is a snap point, so the wheel, the scrollbar, a touch or a row focused by the
 * keyboard leaves the list at rest on the logical pixel grid.
 *
 * As the design draws them (ADR-061's amendment "the kit and the entry forms match the design"), a row sets its
 * name in Noto Sans and may lead with a 12 px glyph, carry a muted sub-line under its name and end with a trailing
 * value in the pixel face; rows that name a section are grouped under its heading in brass, with the section's
 * count at the right. A heading is not an option and takes no focus: each section is a `group` named by it.
 */

/**
 * One row of a list.
 */
export interface ListEntry<T extends string> {
  readonly value: T;
  readonly label: string;
  readonly disabled?: boolean;
  /**
   * The glyph the row leads with.
   */
  readonly glyph?: GlyphName;
  /**
   * A picture the row leads with in the glyph's place, and its height in logical pixels, which the row grows to
   * hold: character select's standing look of each character (ADR-094 decision 27). It is hidden from assistive
   * technology, as a glyph is, since the row's name says whose it is.
   */
  readonly lead?: RowLead;
  /**
   * A line under the name, muted.
   */
  readonly sub?: string;
  /**
   * A short value at the row's right, in the pixel face.
   */
  readonly trailing?: string;
  /**
   * The section the row is listed under. Rows of a section are consecutive; a list whose rows name none has
   * no headings.
   */
  readonly section?: string;
}

/**
 * The rows of a list in runs of one section each, with their indices in the whole list.
 */
interface Section<T extends string> {
  readonly label: string | undefined;
  readonly rows: readonly { readonly item: ListEntry<T>; readonly index: number }[];
}

/**
 * Splits a list's rows into runs of consecutive rows naming the same section.
 *
 * @param items The rows.
 * @returns The runs, in order; one run with no label when no row names a section.
 */
function sectionsOf<T extends string>(items: readonly ListEntry<T>[]): Section<T>[] {
  const out: { label: string | undefined; rows: { item: ListEntry<T>; index: number }[] }[] = [];
  items.forEach((item, index) => {
    const last = out.at(-1);
    if (last !== undefined && last.label === item.section) {
      last.rows.push({ item, index });
    } else {
      out.push({ label: item.section, rows: [{ item, index }] });
    }
  });
  return out;
}

/**
 * A picture a row leads with in the glyph's place, and its height in logical pixels.
 */
export interface RowLead {
  readonly node: ReactNode;
  readonly height: number;
}

/**
 * A row's content: its glyph or the picture it leads with, its name over its sub-line, and its trailing value, each
 * set in the middle of the row's content on whole logical pixels.
 *
 * @param props The row's label, and its `glyph`, `lead`, `sub` line and `trailing` value when it has them.
 * @returns The content.
 */
function RowContent({ label, glyph, lead, sub, trailing }: { readonly label: string; readonly glyph?: GlyphName | undefined; readonly lead?: RowLead | undefined; readonly sub?: string | undefined; readonly trailing?: string | undefined }): ReactElement {
  const metrics = KIT_METRICS.list;
  const content = listRowContent({ glyph: glyph !== undefined, sub: sub !== undefined, trailing: trailing !== undefined, ...(lead === undefined ? {} : { lead: lead.height }) });
  const text = sub !== undefined ? metrics.name + metrics.subGap + metrics.sub : metrics.name;
  return (
    <>
      {lead === undefined ? null : (
        <span className="ot-row__lead" data-kit="row-lead" aria-hidden="true" style={{ marginTop: logical(centreOffset(content, lead.height)) }}>
          {lead.node}
        </span>
      )}
      {glyph === undefined || lead !== undefined ? null : (
        <span className="ot-row__glyph" style={{ marginTop: logical(centreOffset(content, metrics.glyph)) }}>
          <Glyph name={glyph} />
        </span>
      )}
      <span className="ot-row__text" style={{ marginTop: logical(centreOffset(content, text)), gap: logical(metrics.subGap) }}>
        <span className="ot-row__name">{label}</span>
        {sub === undefined ? null : <span className="ot-row__sub">{sub}</span>}
      </span>
      {trailing === undefined ? null : (
        <span className="ot-row__trail" style={{ marginTop: logical(centreOffset(content, metrics.trail)) }}>
          {trailing}
        </span>
      )}
    </>
  );
}

/**
 * One row: an `option` with its name in Noto Sans, led by its glyph and followed by its trailing value when it
 * has them, its sub-line under the name. Hover and a press show on its background, the selected row in the
 * selected fill inside a brass edge that every row keeps, unseen, so selecting a row changes no size, keyboard
 * focus as the aether outline inside its box, and a disabled row in the disabled text colour. The glyph, the
 * text and the trailing value are each set in the middle of the row's content on whole logical pixels.
 *
 * @param props The row's label; its `glyph` or `lead`, `sub` line and `trailing` value, when it has them; whether it is
 *   `selected`, `disabled` or the list's tab stop; a ref setter for moving focus to it; `onPick`, run when it
 *   is clicked or Space selects it; and `onActivate`, run when Enter acts on it.
 * @returns The row.
 */
export function ListRow({ label, glyph, lead, sub, trailing, selected, disabled = false, tabStop, refer, onPick, onActivate }: { readonly label: string; readonly glyph?: GlyphName | undefined; readonly lead?: RowLead | undefined; readonly sub?: string | undefined; readonly trailing?: string | undefined; readonly selected: boolean; readonly disabled?: boolean; readonly tabStop: boolean; readonly refer?: (element: HTMLDivElement | null) => void; readonly onPick: () => void; readonly onActivate?: () => void }): ReactElement {
  const { state, handlers } = useInteraction({ disabled, focus: "visible" });
  const metrics = KIT_METRICS.list;
  const shape = { glyph: glyph !== undefined, sub: sub !== undefined, trailing: trailing !== undefined, ...(lead === undefined ? {} : { lead: lead.height }) };

  /**
   * Space selects the focused row and Enter acts on it; either key goes no further than the row. An
   * auto-repeat of either does nothing, so a held Enter acts on the row once, as a held Enter clicks a kit
   * control once (`useInteraction`).
   *
   * @param event The key press on the row.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== " " && event.key !== "Enter") {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (disabled || event.repeat) {
      return;
    }
    if (event.key === " ") {
      onPick();
    } else {
      onActivate?.();
    }
  };

  return (
    <div
      ref={refer}
      role="option"
      aria-selected={selected}
      aria-disabled={disabled ? true : undefined}
      tabIndex={tabStop ? 0 : -1}
      className={classes("ot-row", state.hover && "is-hover", state.pressed && "is-pressed", state.focus && "is-focus", disabled && "is-disabled", selected && "is-selected")}
      data-kit="list-row"
      style={{ height: logical(listRowHeight(shape)), borderWidth: logical(metrics.edge), paddingLeft: logical(metrics.paddingX), paddingRight: logical(metrics.paddingX), paddingTop: logical(metrics.paddingY), gap: logical(metrics.glyphGap) }}
      {...handlers}
      onClick={disabled ? undefined : onPick}
      onKeyDown={onKeyDown}
    >
      <RowContent label={label} glyph={glyph} lead={lead} sub={sub} trailing={trailing} />
    </div>
  );
}

/**
 * A button drawn as a list's row, set after the listbox in the same frame (ADR-061's amendment "character select as
 * redesigned"): its glyph, its name and its sub-line as a row lays them out, in the accent tone (the glyph in
 * `brass-200`, the name in `brass-100`), with the row's hover, press and focus. It is a button, not an option, so the
 * listbox's arrow keys never reach it and selecting a row never presses it; it takes its own place in the tab order,
 * and a click, Enter or Space presses it once.
 *
 * @param props The row's `label`, its `glyph` and `sub` line when it has them, and `onPress`.
 * @returns The button.
 * @example
 * <ListAction label="New character" glyph="user-plus" sub="1 of 3 slots used" onPress={create} />
 */
export function ListAction({ label, glyph, sub, onPress }: { readonly label: string; readonly glyph?: GlyphName; readonly sub?: string; readonly onPress: () => void }): ReactElement {
  const { state, handlers } = useInteraction({ disabled: false, focus: "visible" });
  const metrics = KIT_METRICS.list;
  const shape = { glyph: glyph !== undefined, sub: sub !== undefined, trailing: false };

  /**
   * Enter and Space press the button; an auto-repeat does nothing, so a held key presses it once.
   *
   * @param event The key press on the button.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== " " && event.key !== "Enter") {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (!event.repeat) {
      onPress();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className={classes("ot-row", "ot-row--accent", state.hover && "is-hover", state.pressed && "is-pressed", state.focus && "is-focus")}
      data-kit="list-action"
      style={{ height: logical(listRowHeight(shape)), borderWidth: logical(metrics.edge), paddingLeft: logical(metrics.paddingX), paddingRight: logical(metrics.paddingX), paddingTop: logical(metrics.paddingY), gap: logical(metrics.glyphGap) }}
      {...handlers}
      onClick={onPress}
      onKeyDown={onKeyDown}
    >
      <RowContent label={label} glyph={glyph} sub={sub} />
    </div>
  );
}

/**
 * What a list shows when it has no rows, as the design draws it: a panel holding, centred, a glyph, a title in the
 * pixel face and one sentence in running text, the glyph and the title only when given. P1.4-T6's character
 * select, its Now consumer, passes neither and no action: an account with no character goes to the name step,
 * and creating a further character is an action of its own beside the list (P1.4-T28).
 *
 * @param props The sentence when there is one, and the `glyph` and `title` when there are any.
 * @returns The empty state.
 */
export function EmptyState({ glyph, title, children }: { readonly glyph?: GlyphName; readonly title?: string; readonly children?: ReactNode }): ReactElement {
  const metrics = KIT_METRICS.empty;
  return (
    <div className="ot-empty ot-ui-frame-panel" data-kit="empty" style={{ padding: `${logical(metrics.paddingY)} ${logical(metrics.paddingX)}`, gap: logical(metrics.gap) }}>
      {glyph === undefined ? null : (
        <CentredRow>
          <Glyph name={glyph} />
        </CentredRow>
      )}
      {title === undefined ? null : <p className="ot-empty__title">{title}</p>}
      {children === undefined ? null : <p className="ot-empty__text">{children}</p>}
    </div>
  );
}

/**
 * A section's heading inside a list: its label in the pixel face in brass and its row count at the right, muted.
 * It is hidden from assistive technology, which reads the section's `group` by the same label.
 *
 * @param props The label and the count.
 * @returns The heading.
 */
function SectionHeading({ label, count }: { readonly label: string; readonly count: number }): ReactElement {
  const metrics = KIT_METRICS.list;
  return (
    <div className="ot-list__heading" data-kit="list-heading" aria-hidden="true" style={{ height: logical(metrics.heading), paddingTop: logical(metrics.headingTop), paddingLeft: logical(metrics.paddingX + metrics.edge), paddingRight: logical(metrics.paddingX + metrics.edge) }}>
      <span>{label}</span>
      <span className="ot-list__count">{count}</span>
    </div>
  );
}

/**
 * A list to choose from.
 *
 * @param props The list's accessible `label`; its `items`; the selected `value`, or null for none;
 *   `onChange` with the value selected; `onActivate` with the value Enter acts on; `empty`, the empty state
 *   shown when there are no items; and its `width` and `height` in logical pixels, the list scrolling
 *   inside a height its rows exceed.
 * @returns The listbox, or the empty state.
 * @example
 * <List label={label} items={characters} value={chosen} onChange={setChosen} onActivate={play} empty={<EmptyState>{sentence}</EmptyState>} />
 */
export function List<T extends string>({ label, items, value, onChange, onActivate, empty, width, height }: { readonly label: string; readonly items: readonly ListEntry<T>[]; readonly value: T | null; readonly onChange: (value: T) => void; readonly onActivate?: (value: T) => void; readonly empty: ReactNode; readonly width?: number; readonly height?: number }): ReactElement {
  const rows = useRef<(HTMLDivElement | null)[]>([]);
  const disabled = items.map((item) => item.disabled === true);
  const chosen = items.findIndex((item) => item.value === value);
  const tabStop = chosen >= 0 && disabled[chosen] === false ? chosen : nextEnabled(disabled, -1, 1);

  /**
   * Moves the selection with the up and down arrows, Home and End, and focuses the row selected.
   *
   * @param event The key press on a row.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const from = rows.current.findIndex((row) => row === event.target);
    const to = choiceForKey(event.key, disabled, from, "vertical");
    if (to === null || to < 0) {
      return;
    }
    event.preventDefault();
    onChange((items[to] as ListEntry<T>).value);
    rows.current[to]?.focus();
  };

  const size = { width: width === undefined ? undefined : logical(width) };
  if (items.length === 0) {
    return (
      <div className="ot-list" data-kit="list" style={size}>
        {empty}
      </div>
    );
  }
  return (
    <div className="ot-list" data-kit="list" style={size}>
      <div role="listbox" aria-label={label} className="ot-list__box ot-scroll" data-kit="listbox" style={{ height: height === undefined ? undefined : logical(height) }} onKeyDown={onKeyDown}>
        {sectionsOf(items).map((section, at) => {
          const drawn = section.rows.map(({ item, index }) => (
            <ListRow
              key={item.value}
              label={item.label}
              glyph={item.glyph}
              lead={item.lead}
              sub={item.sub}
              trailing={item.trailing}
              selected={index === chosen}
              disabled={item.disabled === true}
              tabStop={index === tabStop}
              refer={(element) => {
                rows.current[index] = element;
              }}
              onPick={() => onChange(item.value)}
              {...(onActivate === undefined ? {} : { onActivate: () => onActivate(item.value) })}
            />
          ));
          if (section.label === undefined) {
            return drawn;
          }
          return (
            <div key={`${at}:${section.label}`} role="group" aria-label={section.label} className="ot-list__section" data-kit="list-section">
              <SectionHeading label={section.label} count={section.rows.length} />
              {drawn}
            </div>
          );
        })}
      </div>
    </div>
  );
}
